<template>
  <div id="hanteraTipsContainer">
    <template v-if="editData || isLoadingEdit">
      <Spinner v-if="isLoadingEdit" />
      <div v-else-if="editLoadError" class="alert alert-danger" role="alert">
        <strong>Något gick fel.</strong> Det gick inte att hämta redigeringsdata. Försök igen senare.
      </div>
      <hantera-tips-edit
        v-else-if="editData"
        :tips="editData"
        :content-id="contentId"
        @back="onEditBack"
        @done="onEditDone"
      />
    </template>

    <template v-else-if="preview">
      <Spinner v-if="isLoadingPreview" />
      <div v-else-if="previewError" class="alert alert-danger" role="alert">
        <strong>Något gick fel.</strong> Det gick inte att hämta tipset. Försök igen senare.
      </div>
      <hantera-tips-preview
        v-else
        :tips="preview"
        :action-error="actionError"
        @back="closePreview"
        @delete="onDelete"
        @publish="onPublish"
        @unpublish="onUnpublish"
        @update-date="onUpdateDate"
        @edit="onEdit"
        @dismiss-error="actionError = null"
      />
    </template>

    <template v-else>
      <div class="tipslista__kontroller tipslista__filter">
        <Spinner v-if="isLoadingFilter" />
        <tips-filter
          v-if="config"
          :config="config"
          v-model:freeText="freeText"
          :selected-values="selectedValues"
          @submit="submitFilter"
          @reset="resetFilters"
        />

        <div class="sortering-dropdown dropdown" :class="{ open: sortDropdownOpen }">
          <span>Sortering:</span>
          <button
            class="btn btn-default dropdown-toggle"
            type="button"
            aria-haspopup="true"
            :aria-expanded="sortDropdownOpen"
            :aria-label="`Sortering, valt alternativ, ${currentSortLabel}, tryck för att expandera lista med fler alternativ.`"
            @click.stop="sortDropdownOpen = !sortDropdownOpen"
          >
            <span>{{ currentSortLabel }} {{  }}</span>
            <span class="caret"></span>
          </button>
          <ul class="dropdown-menu sortering-dropdown--lista" @click.stop>
            <li v-for="opt in sortOptions" :key="opt.value">
              <button
                class="sortering-dropdown--alt"
                :class="{ aktiv: sort === opt.value }"
                type="button"
                @click="selectSort(opt.value)"
              >{{ opt.label }}</button>
            </li>
          </ul>
        </div>
      </div>

      <Spinner v-if="isLoadingResults" />
      <div v-else-if="resultsError" class="alert alert-danger" role="alert">
        <strong>Något gick fel.</strong> Det gick inte att hämta resultaten. Försök igen senare.
      </div>
      <template v-else-if="results">
        <div v-if="successMessage" class="alert alert-success alert-dismissible" role="alert">
          <button type="button" class="close" @click="successMessage = null" aria-label="Stäng">
            <span aria-hidden="true">&times;</span>
          </button>
          <strong>Klart!</strong> {{ successMessage }}
        </div>

        <span class="sr-only" aria-live="polite" aria-atomic="true">Resultat: {{ results.TotalCount }} {{ results.TotalCount === 1 ? 'träff' : 'träffar' }}</span>
        <h2 id="post-heading" class="antalresultat">Resultat: <span>{{ results.TotalCount }} {{ results.TotalCount === 1 ? 'träff' : 'träffar' }}</span></h2>

        <hantera-tips-list v-if="results.TotalCount > 0" :items="results.Items" @open-preview="openPreview" />

        <tips-pagination
          v-if="results.PageCount > 1"
          :current-page="results.CurrentPage"
          :page-count="results.PageCount"
          @page-change="goToPage"
        />
      </template>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, reactive, ref } from 'vue';
import TipsFilter from './TipsFilter.vue';
import HanteraTipsList from './HanteraTipsList.vue';
import HanteraTipsPreview from './HanteraTipsPreview.vue';
import HanteraTipsEdit from './HanteraTipsEdit.vue';
import TipsPagination from '../components/Pagination.vue';
import Spinner from '../components/spinner.vue';
import {
  FilterConfigDto,
  HanteraTipsEditDto,
  HanteraTipsItemDto,
  HanteraTipsPreviewDto,
  HanteraTipsResultsDto,
  deleteTips,
  fetchEditData,
  fetchFilterConfig,
  fetchPreview,
  fetchResults,
  publishTips,
  unpublishTips,
  updateTipsDate,
} from './hanteraTipsSidaService';

const sortOptions = [
  { value: 'inkAsc',  label: 'Inkommet (äldst först)' },
  { value: 'inkDesc', label: 'Inkommet (nyast först)' },
  { value: 'pubAsc',  label: 'Publicerat (äldst först)' },
  { value: 'pubDesc', label: 'Publicerat (nyast först)' },
];

export default defineComponent({
  name: 'HanteraTipsSida',
  components: { TipsFilter, HanteraTipsList, HanteraTipsPreview, HanteraTipsEdit, TipsPagination, Spinner },
  props: {
    contentId: { type: Number, required: true },
  },
  setup(props) {
    const config = ref<FilterConfigDto | null>(null);
    const results = ref<HanteraTipsResultsDto | null>(null);
    const isLoadingFilter = ref(true);
    const isLoadingResults = ref(true);
    const resultsError = ref(false);
    const freeText = ref('');
    const selectedValues = reactive<Record<string, string[]>>({});
    const sort = ref('inkAsc');
    const currentPage = ref(1);
    let resultsAbortController: AbortController | null = null;

    const preview = ref<HanteraTipsPreviewDto | null>(null);
    const isLoadingPreview = ref(false);
    const previewError = ref(false);
    const actionError = ref<string | null>(null);
    const successMessage = ref<string | null>(null);

    const editData = ref<HanteraTipsEditDto | null>(null);
    const isLoadingEdit = ref(false);
    const editLoadError = ref(false);

    function buildQueryParams(): URLSearchParams {
      const params = new URLSearchParams();
      if (freeText.value) params.set('fsq', freeText.value);
      for (const [key, values] of Object.entries(selectedValues)) {
        values.forEach(v => params.append(key, v));
      }
      if (sort.value) params.set('sort', sort.value);
      return params;
    }

    async function loadFilterConfig() {
      try {
        const data = await fetchFilterConfig(props.contentId);
        config.value = data;
        data.FilterElements.forEach((el) => {
          if (!selectedValues[el.QueryStringParameter]) {
            selectedValues[el.QueryStringParameter] = [];
          }
        });
      } finally {
        isLoadingFilter.value = false;
      }
    }

    async function loadResults() {
      resultsAbortController?.abort();
      resultsAbortController = new AbortController();
      isLoadingResults.value = true;
      resultsError.value = false;
      try {
        const params = buildQueryParams();
        if (currentPage.value > 1) params.set('page', String(currentPage.value));
        results.value = await fetchResults(props.contentId, params, resultsAbortController.signal);
        isLoadingResults.value = false;
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          resultsError.value = true;
          isLoadingResults.value = false;
        }
      }
    }

    async function openPreview(item: HanteraTipsItemDto) {
      preview.value = {} as HanteraTipsPreviewDto;
      isLoadingPreview.value = true;
      previewError.value = false;
      try {
        preview.value = await fetchPreview(props.contentId, item.FormSubmissonId, item.Id);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        previewError.value = true;
        preview.value = null;
      } finally {
        isLoadingPreview.value = false;
      }
    }

    function closePreview(message: string | null = null) {
      preview.value = null;
      actionError.value = null;
      successMessage.value = message;
      loadResults();
    }

    async function onDelete(tips: HanteraTipsPreviewDto) {
      actionError.value = null;
      try {
        await deleteTips(props.contentId, tips.FormSubmissonId, tips.Id);
        closePreview('Tipset har tagits bort.');
      } catch {
        actionError.value = 'Det gick inte att ta bort tipset. Försök igen senare.';
      }
    }

    async function onPublish(tips: HanteraTipsPreviewDto) {
      actionError.value = null;
      try {
        await publishTips(props.contentId, tips.FormSubmissonId, tips.Id);
        closePreview('Tipset har publicerats.');
      } catch {
        actionError.value = 'Det gick inte att publicera tipset. Försök igen senare.';
      }
    }

    async function onUnpublish(tips: HanteraTipsPreviewDto) {
      actionError.value = null;
      try {
        await unpublishTips(props.contentId, tips.Id);
        closePreview('Tipset har avpublicerats.');
      } catch {
        actionError.value = 'Det gick inte att avpublicera tipset. Försök igen senare.';
      }
    }

    async function onUpdateDate(tips: HanteraTipsPreviewDto) {
      actionError.value = null;
      try {
        await updateTipsDate(props.contentId, tips.Id);
        closePreview('Utgångsdatumet har uppdaterats.');
      } catch {
        actionError.value = 'Det gick inte att uppdatera utgångsdatumet. Försök igen senare.';
      }
    }

    async function onEdit(tips: HanteraTipsPreviewDto) {
      isLoadingEdit.value = true;
      editLoadError.value = false;
      try {
        editData.value = await fetchEditData(props.contentId, tips.FormSubmissonId, tips.Id);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        editLoadError.value = true;
      } finally {
        isLoadingEdit.value = false;
      }
    }

    async function onEditBack() {
      const tipsContentId = editData.value?.TipsContentId ?? 0;
      editData.value = null;
      if (tipsContentId > 0) {
        isLoadingPreview.value = true;
        previewError.value = false;
        try {
          preview.value = await fetchPreview(props.contentId, null, tipsContentId);
        } catch {
          previewError.value = true;
          preview.value = null;
        } finally {
          isLoadingPreview.value = false;
        }
      }
    }

    function onEditDone(message: string) {
      editData.value = null;
      closePreview(message);
    }

    const currentSortLabel = computed(() => sortOptions.find(o => o.value === sort.value)?.label ?? '');
    const sortDropdownOpen = ref(false);

    function closeSortDropdown() { sortDropdownOpen.value = false; }
    onMounted(() => document.addEventListener('click', closeSortDropdown));
    onUnmounted(() => document.removeEventListener('click', closeSortDropdown));

    function selectSort(value: string) {
      sort.value = value;
      sortDropdownOpen.value = false;
      submitFilter();
    }

    function submitFilter() {
      currentPage.value = 1;
      loadResults();
    }

    function resetFilters() {
      freeText.value = '';
      Object.keys(selectedValues).forEach((key) => { selectedValues[key] = []; });
      sort.value = 'inkAsc';
      currentPage.value = 1;
      loadResults();
    }

    function goToPage(page: number) {
      if (!results.value || page < 1 || page > results.value.PageCount) return;
      currentPage.value = page;
      loadResults();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    onMounted(() => {
      loadFilterConfig();
      loadResults();
    });

    return {
      config,
      results,
      isLoadingFilter,
      isLoadingResults,
      resultsError,
      freeText,
      selectedValues,
      sort,
      sortOptions,
      currentSortLabel,
      sortDropdownOpen,
      selectSort,
      submitFilter,
      resetFilters,
      goToPage,
      preview,
      isLoadingPreview,
      previewError,
      actionError,
      successMessage,
      openPreview,
      closePreview,
      onDelete,
      onPublish,
      onUnpublish,
      onUpdateDate,
      onEdit,
      editData,
      isLoadingEdit,
      editLoadError,
      onEditBack,
      onEditDone,
    };
  },
});
</script>
